import { GET_EX_CO_TENANT_DETAILS } from '../../constants/reactQueryKeys';
import { EXPRESS_CHECKOUT_ACTION } from '../../constants/newRelic';
import { ExpressCheckoutWrapper } from '../ExpressCheckoutWrapper';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Span, Spinner, VisuallyHidden } from '@vp/swan';
import { useSiteData } from '../../hooks/useSiteData';
import { getCartTenantDetails } from '@vp/om-express-checkout/utils';
import { useUserContext } from '@vp/ubik-context';
import { useSourcePageContext } from '../contexts/SourcePageContext';
import { useLineItemData } from '../contexts/LineItemDataContext';
import { useLineItemActions } from '../contexts/LineItemActionsContext';
import { reorderForAltCheckout } from '../../apis/oreosApi';
import { useShopperId } from '../../hooks/useShopperId';
import {
  trackExpressCheckoutProductAddedToCart,
  trackBuyNowAddToCartFailed,
  trackExpressCheckoutAddToCartClick,
} from '../../apis/newrelic/newRelicTrackingApi';
import { useTranslations } from '@vp/om-translations';
import { useReorderQuantity } from '../../hooks/useReorderQuantity';
import { useLineItemReorderActionStatus } from '../contexts/LineItemReorderActionStatusContext';
import { useFlawedAddToCartTracker } from '../../hooks/useFlawedAddToCartTracker';

export const ItemBuyNowButton = ({ pageName }: { pageName: string }) => {
  const { tenant: brand } = useUserContext();
  const {
    locale,
    fragmentName,
    tenantEnvironment,
    vistacartTenant: vistacartTenantName,
  } = useSiteData();

  const { sourcePage } = useSourcePageContext();
  const item = useLineItemData();
  const lineItemActions = useLineItemActions();
  const { setAddToCartFailure, resetReorderActionFeedback } =
    useLineItemReorderActionStatus();
  const shopperId = useShopperId();

  const buyNowBtnRef = useRef<HTMLButtonElement>(null);
  const [ctaHasBeenClicked, setCtaHasBeenClicked] = useState(false);
  const { quantity } = useReorderQuantity();

  const [orderHistoryMessages = {}] = useTranslations(['orderHistoryMessages']);
  const [sharedActionMessages = {}] = useTranslations(['sharedActionMessages']);
  const analyticsMetadata = useMemo(
    () => ({
      source: sourcePage,
      siteName: fragmentName,
      tenant: vistacartTenantName,
      shopperId,
    }),
    [shopperId, vistacartTenantName, fragmentName, sourcePage]
  );

  const [showExpressCheckoutFlyout, setShowExpressCheckoutFlyout] =
    useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [cartDetails, setCartDetails] = useState<{
    type: string;
    altCartId: string;
  } | null>(null);

  const queryClient = useQueryClient();
  const queryKey = [GET_EX_CO_TENANT_DETAILS, brand, locale, tenantEnvironment];
  const trackAddToCartError = useFlawedAddToCartTracker();

  const { data: cartTenantDetails, refetch } = useQuery({
    queryKey: queryKey,

    queryFn: async () => {
      try {
        return await getCartTenantDetails(
          brand,
          locale,
          tenantEnvironment.toLowerCase()
        );
      } catch (error) {
        console.error('Error fetching cart tenant details', error);
        throw error;
      }
    },

    enabled: false,
  });

  const onBuyNowClick = async () => {
    resetReorderActionFeedback(EXPRESS_CHECKOUT_ACTION);

    setIsLoading(true);
    setCtaHasBeenClicked(true);

    trackExpressCheckoutAddToCartClick({
      itemId: item.id,
      vistacartTenantName,
      page: pageName,
    });

    // only refetch if the data is not already cached
    // this is to avoid unnecessary network requests
    if (!queryClient.getQueryData(queryKey)) {
      await refetch();
    }
    //Validate required fields before proceeding
    if (!item.id || !item.productKey) {
      setIsLoading(false);
      return;
    }

    const reorderItems = [
      {
        lineItemId: item.id,
        quantity: quantity || 0,
        analytics: {
          source: sourcePage,
        },
        displayPosition: lineItemActions.displayPosition || 0,
        productId: item.productKey,
      },
    ];

    // prep alt cart for checkout
    try {
      const { altCartId } = await reorderForAltCheckout({
        tenantEnvironment,
        orderNumber: item.orderNumber,
        reorderItems,
        vistacartTenantName,
        reorderSourcePage: sourcePage,
        reorderSourcePageName: pageName,
        shopperId,
      });
      if (altCartId) {
        setCartDetails({
          type: 'alt',
          altCartId,
        });
        setShowExpressCheckoutFlyout(true);
      }

      trackExpressCheckoutProductAddedToCart({
        lineItemId: item.id,
        displayName: item.productName || '',
        productKey: item.productKey || '',
        pageName,
      });
    } catch (e) {
      trackAddToCartError(e);
      setAddToCartFailure(true);
      if (e instanceof Error) {
        trackBuyNowAddToCartFailed(e, analyticsMetadata);
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Focus restoration effect
  useEffect(() => {
    if (
      !showExpressCheckoutFlyout &&
      ctaHasBeenClicked &&
      buyNowBtnRef.current
    ) {
      buyNowBtnRef.current.focus();
    }
  }, [showExpressCheckoutFlyout, ctaHasBeenClicked]);

  return (
    <>
      <Button
        skin="primary"
        onClick={onBuyNowClick}
        loading={isLoading}
        ref={buyNowBtnRef}
      >
        {isLoading && (
          <Spinner
            accessibleText={sharedActionMessages?.['loading']}
            aria-busy="true"
            aria-live="polite"
            role="status"
            size="standard"
          />
        )}
        <Span>{orderHistoryMessages.buyNowButtonText}</Span>
        <VisuallyHidden>{`: ${item.productName}`}</VisuallyHidden>
      </Button>
      {showExpressCheckoutFlyout &&
        cartTenantDetails &&
        !isLoading &&
        cartDetails && (
          <ExpressCheckoutWrapper
            altCartId={cartDetails.altCartId}
            cartTenantDetails={cartTenantDetails}
            showExpressCheckoutFlyout={showExpressCheckoutFlyout}
            setShowExpressCheckoutFlyout={setShowExpressCheckoutFlyout}
            pageName={pageName}
          />
        )}
    </>
  );
};
